import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import {
  AllCommunityModule,
  CellEditingStoppedEvent,
  CellKeyDownEvent,
  type ColDef,
  DataTypeDefinition,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  type RowSelectionOptions,
  themeQuartz
} from "ag-grid-community";
import { ColumnDto, TableDataType, TableMenuOption, TableMeta } from "../../models/table-meta.interface";
import { CustomLoadingOverlayComponent } from "./custom-loading-overlay";
import { CustomNoDataFound } from "./custom-no-data-found";
import { AgGridTableCellComponent } from "./ag-grid-table-cell/ag-grid-table-cell.component";
import { AgGridAngular } from "ag-grid-angular";
import { GridTableService } from "../../services/grid-table.service";
import { LoadingService } from "../../services/loading.service";
import { MultiSelectComponent } from "../select/multi-select.component";
import { DynamicFromDataType } from "../../constants/dynamic-from-data-type";
import { AutocompleteDropdownComponent } from "../autocomplete-dropdown/autocomplete-dropdown.component";
import { DatePickerComponent } from "../common/date-picker/date-picker.component";
import { DynamicFormService } from "../../services/dynamic-form.service";
import { FormBuilder } from "@angular/forms";
import { InputComponent } from "../input/input.component";
import { AgGridFilterComponent } from "./ag-grid-filter/ag-grid-filter.component";

export const AgGridDataTypes = {
  action: "action",
  menuOptions: "menuOptions",
  booleanToggle: "booleanToggle",
  all: () => {
    return [AgGridDataTypes.action, AgGridDataTypes.menuOptions, AgGridDataTypes.booleanToggle];
  }
};

// Lazy load AG Grid modules only when needed
ModuleRegistry.registerModules([AllCommunityModule]);

const DEFAULT_ACTION_COL: ColDef = {
  field: AgGridDataTypes.action,
  cellDataType: AgGridDataTypes.action,
  sortable: false,
  pinned: "right",
  lockPinned: true,
  resizable: false,
  lockVisible: true,
  filter: false,
  headerName: "",
  width: 20,
  cellRenderer: AgGridTableCellComponent,
  cellRendererParams: {}
};

@Component({
  selector: "app-ag-grid-wrapper",
  standalone: true,
  imports: [
    AgGridAngular
  ],
  templateUrl: "./ag-grid-wrapper.component.html",
  styleUrl: "./ag-grid-wrapper.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush // Add OnPush for better performance
})
export class AgGridWrapperComponent implements OnDestroy {
  public gridApi!: GridApi;

  private resizeObserver?: ResizeObserver;
  private resizeTimeout?: number;
  private gridContainer?: HTMLElement;
  private previousWidth: number = 0;

  isLoading: boolean = false;
  defaultMaxWidth = 500;
  defaultMinWidth = 80;
  tableColumns: ColDef[] = [];
  defaultColDef: ColDef = {
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      fontWeight: 300
    },
    headerStyle: {},
    // suppressKeyboardEvent: params => {
    //   const key = (params.event as KeyboardEvent).key;
    //   return params.editing && key === "Enter";
    // }
  };
  dataTypeDefinitions: { [p: string]: DataTypeDefinition<any> } | undefined = {
    [AgGridDataTypes.action]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [AgGridDataTypes.menuOptions]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [TableDataType.STRING]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [TableDataType.BOOLEAN]: {
      extendsDataType: "boolean",
      baseDataType: "boolean"
    },
    [TableDataType.ARRAY]: {
      extendsDataType: "object",
      baseDataType: "object"
    },
    [TableDataType.OBJECT]: {
      extendsDataType: "object",
      baseDataType: "object"
    },
    [TableDataType.DATETIME]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [TableDataType.DATE]: {
      extendsDataType: "text",
      baseDataType: "text"
    },
    [TableDataType.NUMBER]: {
      extendsDataType: "text",
      baseDataType: "text"
    }
  };
  rowSelection: RowSelectionOptions | "single" | "multiple" = {
    mode: "multiRow",
    enableClickSelection: true
    // enableSelectionWithoutKeys: true
  };
  selectionColumnDef = {};
  initialState = {};
  tosTheme = themeQuartz.withParams({
    accentColor: "#1062B9",
    borderColor: "#F2F4F7",
    browserColorScheme: "light",
    cellTextColor: "#101828",
    columnBorder: false,
    dataFontSize: 13,
    fontFamily: {
      googleFont: "Inter"
    },
    foregroundColor: "#101828",
    headerBackgroundColor: "#F5F5F4",
    headerColumnResizeHandleHeight: "25%",
    headerColumnResizeHandleWidth: "20px",
    headerFontSize: 12,
    headerFontWeight: 400,
    headerHeight: 44,
    rowHeight: 55,
    headerRowBorder: true,
    rowBorder: true,
    rowHoverColor: "#F9FAFB",
    rowVerticalPaddingScale: 1.5,
    spacing: 8,
    wrapperBorder: true,
    wrapperBorderRadius: 0
  });
  loadingOverlayComponent = CustomLoadingOverlayComponent;
  readonly CustomNoDataFound = CustomNoDataFound;

  @Input() tableRows: any[] = [];
  @Input() tableMeta!: TableMeta;
  @Input() tableMenuOptions: TableMenuOption[] = [];
  @Input() showMenuOptions: boolean = true;
  @Input() orderBy: { [key: string]: "asc" | "desc" } = {};
  @Input() domLayout: "autoHeight" | "normal" | "print" = "normal";
  @Input() deletionAllowed: boolean = false;
  @Input() selectedItems: any[] = [];
  @Input() allTableRecordsSelected: boolean = false;
  @Input() skipDotNotationLookup: boolean = false;

  @Output() selectedItemsChange = new EventEmitter<any[]>();
  @Output() rowActionTriggered = new EventEmitter<any>();
  @Output() rowDeleted = new EventEmitter<any>();
  @Output() tableMenuOptionSelected = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<any>();
  @Output() cellValueChanged = new EventEmitter<any>();
  @Output() cellEditingStopped = new EventEmitter<CellEditingStoppedEvent>();


  constructor(private gridTableService: GridTableService,
              private loadingService: LoadingService,
              private cdr: ChangeDetectorRef,
              private dynamicFormService: DynamicFormService,
              private fb: FormBuilder) {
  }

  ngOnChanges(changes: any): void {
    if (changes["tableRows"]) {
      // keep columns stable on data changes
    }

    // Sync grid selection when inputs change from parent
    if ((changes["selectedItems"] || changes["allTableRecordsSelected"]) && this.gridApi) {
      // If all selected flag changes
      if (changes["allTableRecordsSelected"]) {
        if (this.allTableRecordsSelected) {
          this.gridApi.selectAll();
          return;
        } else {
          this.gridApi.deselectAll();
          // fall through to apply specific selections if any
        }
      }

      // Apply specific selections based on selectedItems
      if (!this.allTableRecordsSelected) {
        if (!this.selectedItems || this.selectedItems.length === 0) {
          this.gridApi.deselectAll();
        } else {
          const selectedList = this.selectedItems;
          const uniqueColumns = this.tableMeta?.uniqueColumns ?? [];
          this.gridApi.forEachNode((node) => {
            const record = node.data;
            const shouldBeSelected = uniqueColumns.length > 0
              ? selectedList.some((s) => uniqueColumns.every((key: string) => s?.[key] === record?.[key]))
              : selectedList.some((s) => s?.id === record?.id);
            if (node.isSelected() !== shouldBeSelected) {
              node.setSelected(shouldBeSelected);
            }
          });
        }
      }
    }
  }

  ngOnInit() {
    this.updateTableColumns();

    this.loadingService.loading$
      .subscribe({
        next: data => {
          this.isLoading = data.isLoading && data.type === "inline";
          this.optimiseColumnWidth(); //on every load optimise the col width
          this.cdr.detectChanges();
        }
      });

    this.gridTableService.getGridState$().subscribe((savedState) => {
      if (savedState) {
        this.initialState = savedState;
      }
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridTableService.gridInit(this.gridApi, (colId) => this.tableMeta.columns.find(c => c.fieldName === colId));
    this.optimiseColumnWidth(); //once grid is ready optimise the width
    this.setupResizeObserver();
  }

  private setupResizeObserver() {
    // Get the grid container element - try multiple approaches
    let gridElement: HTMLElement | null = null;

    // Try to get from grid API if available
    if (this.gridApi && (this.gridApi as any).gridElement) {
      gridElement = (this.gridApi as any).gridElement;
    }

    // Fallback to querySelector
    if (!gridElement) {
      gridElement = document.querySelector('.ag-root-wrapper') as HTMLElement;
    }

    if (!gridElement) {
      // Fallback: try to get it after a short delay
      setTimeout(() => this.setupResizeObserver(), 100);
      return;
    }

    this.gridContainer = gridElement;

    // Create ResizeObserver to watch for horizontal size changes
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;

        // Only optimize if width actually changed and is valid
        if (width > 0 && width !== this.previousWidth) {
          this.previousWidth = width;

          // Debounce to avoid excessive calls during resize
          if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
          }

          this.resizeTimeout = window.setTimeout(() => {
            if (this.gridApi) {
              this.optimiseColumnWidth();
            }
          }, 150); // 150ms debounce
        }
      }
    });

    // Start observing the grid container
    this.resizeObserver.observe(gridElement);
  }

  ngOnDestroy() {
    // Clean up ResizeObserver
    if (this.resizeObserver && this.gridContainer) {
      this.resizeObserver.unobserve(this.gridContainer);
      this.resizeObserver.disconnect();
    }

    // Clear any pending timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  private optimiseColumnWidth() {
    if (this?.gridApi) {
      if (this.tableMeta?.columns.length > 8) {
        this.gridApi?.autoSizeAllColumns();
        // After auto-sizing, check for remaining white space and expand last unpinned column
        setTimeout(() => this.fillRemainingSpace(), 200);
      } else {
        this.gridApi?.sizeColumnsToFit();
      }
    }
  }

  private fillRemainingSpace() {
    if (!this.gridApi) return;

    try {
      const displayedColumns = this.gridApi.getAllDisplayedColumns();
      if (!displayedColumns || displayedColumns.length === 0) return;

      const gridWidth = displayedColumns.reduce((total: number, col: any) => {
        return total + (col.getActualWidth() || 0);
      }, 0);

      // Get the grid container width using the grid's DOM element
      const gridElement = document.querySelector('.ag-body-viewport') as HTMLElement;
      const containerWidth = gridElement?.clientWidth || 0;

      // If we can't get the container width, try alternative approach
      if (containerWidth === 0) {
        const gridContainer = document.querySelector('.ag-root-wrapper') as HTMLElement;
        const fallbackWidth = gridContainer?.clientWidth || 0;
        if (fallbackWidth > 0) {
          const remainingSpace = fallbackWidth - gridWidth;
          this.adjustLastColumnWidth(displayedColumns, remainingSpace);
        }
        return;
      }

      const remainingSpace = containerWidth - gridWidth;

      if (remainingSpace > 10) { // Only adjust if there's significant remaining space
        this.adjustLastColumnWidth(displayedColumns, remainingSpace);
      }
    } catch (error) {
      console.warn('Error in fillRemainingSpace:', error);
    }
  }

  private adjustLastColumnWidth(displayedColumns: any[], remainingSpace: number) {
    const unpinnedColumns = displayedColumns.filter((col: any) => !col.getPinned());

    if (unpinnedColumns.length > 0) {
      const lastUnpinnedColumn = unpinnedColumns[unpinnedColumns.length - 1];
      const currentWidth = lastUnpinnedColumn.getActualWidth() || 0;
      const newWidth = Math.max(currentWidth + remainingSpace, 50); // Ensure minimum width

      // Use setColumnWidths with an object to set the width of the specific column
      this.gridApi.setColumnWidths([{
        key: lastUnpinnedColumn.getColId(),
        newWidth: newWidth
      }]);
    }
  }

  saveGridState() {
    const state = this.gridApi.getState();
    this.gridTableService.saveGridState(state);
  }

  onSelectionChanged(): void {
    this.selectedItemsChange.emit(this.gridApi.getSelectedRows());
  };

  handleSort(event: any) {
    if (!this.gridApi)
      return;

    console.log(event);
    if (this.gridApi.getColumnDef(event.columns[0]?.getId())?.sortable) {
      this.sortChange.emit(event.columns[0]?.getId());
    }
  }

  updateTableColumns() {
    const dataColumns = this.buildDataColumns() ?? [];
    const tableActions = this.buildTableActions() ?? [];
    this.tableColumns = [
      ...dataColumns,
      ...tableActions
    ];

    const menuOptions = this.buildMenuOptions();
    if (this.showMenuOptions && this.tableMenuOptions.length > 0) {
      this.tableColumns.push(menuOptions);
    }
  }

  private getDeleteAction(): ColDef {
    return {
      ...DEFAULT_ACTION_COL,
      cellRendererParams: {
        columnDto: {
          fieldName: "",
          displayName: "",
          dataType: "STRING",
          columnType: "ACTION",
          actionConfig: {
            "label": "",
            "actionName": "delete",
            "writeOperation": false
          },
          styleConfig: {
            type: "button",
            icon: "assets/icons/delete_icon.svg",
            "styleProperties": {
              "display": "flex",
              "gap": "4px",
              "alignItems": "center",
              "background": "transparent",
              "color": "#1062B9",
              "fontSize": "14px",
              "fontStyle": "normal",
              "border": "none",
              "fontWeight": 500,
              "lineHeight": "20px"
            },
            iconStyleProperties: {
              background: "var(--error-600)",
              height: "16px",
              width: "16px",
              display: "flex"
            }
          }
        },
        actionClicked: {
          emit: (eventActionAndRecord: any) => {
            // this delete action specific handler
            this.rowDeleted.emit(eventActionAndRecord);
          }
        }
      }
    };
  }

  private buildTableActions(): ColDef[] {
    const actions = (this.tableMeta?.actions ?? []);
    const tableActions: ColDef[] = actions.map(action => ({
      ...DEFAULT_ACTION_COL,
      cellRendererParams: {
        columnDto: action,
        actionClicked: this.rowActionTriggered,
      },
      width: action?.thStyleConfig?.width
    }));
    this.deletionAllowed && tableActions.push(this.getDeleteAction());
    return tableActions;
  }

  private buildDataColumns(): ColDef[] {
    const columns = this.tableMeta?.columns?.map((col, index) => {
      const isLastColumn = index === this.tableMeta.columns.length - 1;
      return {
        field: col.fieldName,
        sortable: !!col.sortable,
        sort: this.orderBy?.[col.fieldName] ?? null,
        sortIndex: null,
        resizable: true,
        filter: !!col.filterable,
        floatingFilter: !!col.filterable && this.tableRows.length > 0,
        floatingFilterComponent: AgGridFilterComponent,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
        type: col.dataType,
        headerName: col.displayName,
        cellDataType: col.dataType,
        editable: col.editable && col.dataType != "booleanToggle", // boolean datatypes don't need to support double click editing
        cellRenderer: AgGridTableCellComponent,
        headerClass: "grid-header",
        refData: col.valueMap,
        cellEditor: this.getCellEditor(col),
        cellEditorParams: {
          col,
          ...col.editableConfig,
          ...col.editableConfig?.lookupDetails,
          showIcon: false, // we don't want to show icons for editable columns
        },
        cellEditorPopup: false,
        cellRendererParams: {
          columnDto: col,
          actionClicked: this.rowActionTriggered,
          skipDotNotationLookup: this.skipDotNotationLookup
        },
        // Add flex to the last column to fill remaining space
        ...(isLastColumn && { flex: 1 }),
        // minWidth: parseInt(col.thStyleConfig?.width || this.defaultMinWidth)
      };
    });
    return columns || [];
  }

  private buildMenuOptions(): ColDef {
    return {
      field: AgGridDataTypes.menuOptions,
      sortable: false,
      hide: !this.showMenuOptions,
      filter: false,
      lockPinned: true,
      lockVisible: true,
      resizable: false,
      lockPosition: "right",
      headerName: "",
      pinned: "right",
      cellClass: "grid-row-menu",
      cellDataType: AgGridDataTypes.menuOptions,
      maxWidth: 60,
      cellRenderer: AgGridTableCellComponent,
      cellRendererParams: {
        menuOptions: this.tableMenuOptions,
        menuOptionSelected: this.tableMenuOptionSelected
      }
    };
  }


  onCellKeyDown(event: CellKeyDownEvent) {
    const ke = event.event as KeyboardEvent;
    if ((ke.ctrlKey || ke.metaKey) && ke.key.toLowerCase() === "c") {
      ke.preventDefault();
      const cellValue = event.value != null ? event.value.toString() : "";
      navigator.clipboard.writeText(cellValue);
    }
  }

  private getCellEditor(col: ColumnDto): any {
    if (col.editableConfig) {
      const { inputType } = col.editableConfig;
      switch (inputType) {
        case DynamicFromDataType.PICKLIST:
          return AutocompleteDropdownComponent;
        case DynamicFromDataType.SELECT:
        case DynamicFromDataType.MULTI_SELECT:
          return MultiSelectComponent;
        case DynamicFromDataType.DATE:
        case DynamicFromDataType.DATETIME_LOCAL:
          return DatePickerComponent;
        case TableDataType.BOOLEAN:
          return MultiSelectComponent;
        default:
          return InputComponent;
      }
    }

    // Fall back to AG Grid's built-in editors based on dataType
    // switch (col.dataType) {
    //   case TableDataType.BOOLEAN:
    //     return "agCheckboxCellEditor";
    //   case TableDataType.DATETIME:
    //   case TableDataType.DATE:
    //     return "agDateCellEditor";
    //   case TableDataType.NUMBER:
    //     return "agNumberCellEditor";
    //   default:
    //     return "agTextCellEditor";
    // }
  }

  onFirstDataRendered(event: FirstDataRenderedEvent<any>) {
    // this.gridApi.autoSizeAllColumns();
  }

  onCellValueChanged(event: any) {
    this.cellValueChanged.emit(event);
  }

  onCellEditingStopped(event: CellEditingStoppedEvent<any>) {
    // if (event.column) {
    //   this.gridApi.autoSizeColumns([event.column.getColId()]);
    // }
    this.cellEditingStopped.emit(event);
  }

  private transformTableData() {
    if (!this.tableRows || !this.tableMeta?.columns) return;

    this.tableRows = this.tableRows.map((item: any) => {
      const transformedItem = { ...item };

      this.tableMeta.columns
        .filter(header => header?.editableConfig?.lookupDetails)
        .forEach((header) => {
          const responseBindLabel = header.editableConfig?.lookupDetails?.responseBindLabel;
          const responseBindValue = header.editableConfig?.lookupDetails?.responseBindValue;
          const bindLabel = header.editableConfig?.lookupDetails?.bindLabel;
          const bindValue = header.editableConfig?.lookupDetails?.bindValue;
          const fieldKey = header?.fieldName;

          if (responseBindLabel && responseBindValue && bindLabel && bindValue && fieldKey) {
            // Get the values from the API response using responseBindLabel and responseBindValue
            const apiValue = item[responseBindValue];
            const apiLabel = item[responseBindLabel];

            if (apiValue !== undefined && apiLabel !== undefined) {
              transformedItem[fieldKey] = {
                key: apiValue,                    // For autocomplete component
                value: apiLabel,                  // For autocomplete component
                [bindLabel]: apiLabel,            // For cell renderer display
                [bindValue]: apiValue             // For cell renderer (if needed)
              };
            }
          }
        });

      return transformedItem;
    });

    console.log("transformedItems: ", this.tableRows);
  }

  /**
   * Transforms table data back to API format by extracting the actual values
   * from the autocomplete objects
   */
  public transformDataForApi(data: any[]): any[] {
    if (!data || !this.tableMeta?.columns) return data;

    return data.map((item: any) => {
      const apiItem = { ...item };

      this.tableMeta.columns
        .filter(header => header?.editableConfig?.lookupDetails)
        .forEach((header) => {
          const bindValue = header.editableConfig?.lookupDetails?.bindValue;
          const fieldKey = header?.fieldName;

          if (bindValue && fieldKey && apiItem[fieldKey]) {
            const fieldData = apiItem[fieldKey];
            if (typeof fieldData === "object" && fieldData !== null) {
              if (fieldData.hasOwnProperty('key')) {
                apiItem[fieldKey] = fieldData.key;
              } else if (fieldData.hasOwnProperty('id')) {
                apiItem[fieldKey] = fieldData['id'];
              } else if (fieldData.hasOwnProperty(bindValue)) {
                apiItem[fieldKey] = fieldData[bindValue];
              } else {
                apiItem[fieldKey] = fieldData;
              }
            } else {
              apiItem[fieldKey] = fieldData;
            }
          }
        });
      return apiItem;
    });
  }
}
